const React = require('react')
const { useEffect, useState } = require('react')
const { useHistory, useLocation } = require('react-router-dom')
const { Container, Col, Row } = require('reactstrap')
const { useBlockchainData } = require('@hooks')
const { useNetworkConfig } = require('@contexts')

const { LoadingSpinner, Boxes } = require('@components')
const { TableHeader, TableFooter } = require('@components/table')
const { TransactionsTable } = require('@components')

require('@styles/pages/transactions/index.css')

const Transactions = () => {
    const history = useHistory()
    const location = useLocation()
    const params = new URLSearchParams(location.search)

    const { network } = useNetworkConfig()
    const { getTransactions, getTransactionsStatistics } = useBlockchainData(network.name)

    const [blockNumber, setBlockNumber] = useState(Number(params.get('block')) || null)
    const [address, setAddress] = useState(params.get('a') ? params.get('a').toLowerCase() : null)
    const [page, setPage] = useState(Number(params.get('p')) || 1)
    const [limit, setLimit] = useState(Number(params.get('ps')) || 25)
    const [transactionsData, setTransactionsData] = useState()
    const [statisticsData, setStatisticsData] = useState([])

    const [loading, setLoading] = useState(false)
    const [totalPages, setTotalPages] = useState(0)

    const [title, setTitle] = useState()
    const [subtitle, setSubtitle] = useState()

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const blockNumberParam = Number(params.get('block'))
        const addressParam = params.get('a') ? params.get('a').toLowerCase() : null
        const pageParam = Number(params.get('p'))
        const limitParam = Number(params.get('ps'))

        if (blockNumberParam && blockNumberParam !== blockNumber) {
            setBlockNumber(blockNumberParam)
        }

        if (addressParam && addressParam !== address) {
            setAddress(addressParam)
        }

        if (pageParam && pageParam > 0 && pageParam !== page) {
            setPage(pageParam)
        }

        if (limitParam && limitParam > 0 && limitParam !== limit) {
            setLimit(limitParam)
        }
    }, [location.search])

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)

                const [transactionsResponse, transactionsStatisticsResponse] = await Promise.all([
                    getTransactions(page, limit, {
                        ...(blockNumber && { blockNumber }),
                        ...(address && { address })
                    }),
                    getTransactionsStatistics()
                ])

                if (transactionsResponse && transactionsStatisticsResponse) {
                    setTransactionsData(transactionsResponse)
                    setTotalPages(transactionsResponse.totalPages)

                    if (blockNumber) {
                        setTitle(
                            `A total of ${transactionsResponse.totalItems.toLocaleString()} transaction${
                                transactionsResponse.totalItems === 1 ? '' : 's'
                            } found`
                        )
                    }
                    if (address) {
                        setTitle(
                            `A total of ${transactionsResponse.totalItems.toLocaleString()} transaction${
                                transactionsResponse.totalItems === 1 ? '' : 's'
                            } found`
                        )

                        setSubtitle(
                            `Showing the last ${
                                transactionsResponse.totalItems < 1000
                                    ? transactionsResponse.totalItems.toLocaleString()
                                    : transactionsResponse.totalItems < 500_000
                                    ? `${(transactionsResponse.totalItems / 1000).toFixed(1)}k`
                                    : '100k'
                            } records`
                        )

                        if (transactionsResponse.totalItems > 500_000) {
                            setTotalPages(500_000 / limit)
                        }
                    }
                    if (!blockNumber && !address) {
                        setTitle(`More than ${transactionsResponse.totalItems.toLocaleString()} 
                                    transactions found`)

                        setSubtitle(
                            `Showing the last ${
                                transactionsResponse.totalItems < 1000
                                    ? transactionsResponse.totalItems.toLocaleString()
                                    : transactionsResponse.totalItems < 500_000
                                    ? `${(transactionsResponse.totalItems / 1000).toFixed(1)}k`
                                    : '500k'
                            } records`
                        )

                        if (transactionsResponse.totalItems > 500_000) {
                            setTotalPages(500_000 / limit)
                        }

                        setStatisticsData([
                            {
                                title: 'Transactions (All Time)',
                                value: transactionsStatisticsResponse.totalTransactions.toLocaleString()
                            },
                            {
                                title: 'Transactions (24h)',
                                value: transactionsStatisticsResponse.todayTransactions.toLocaleString()
                            }
                        ])
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [network, page, limit])

    return (
        <div className='page'>
            <Container>
                <Col>
                    <Row>
                        <div className='page-header'>
                            <text className='page-title'>Transactions</text>
                            {blockNumber && (
                                <div style={{ display: 'block', marginTop: '8px' }}>
                                    <text className='page-title-subcontent'>
                                        For Block&nbsp;
                                        <span
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() => history.push(`/${network.name}/block/${blockNumber}`)}
                                        >
                                            {blockNumber}
                                        </span>
                                    </text>
                                </div>
                            )}
                            {address && (
                                <div style={{ display: 'block', marginTop: '8px' }}>
                                    <text className='page-title-subcontent'>
                                        For&nbsp;
                                        <span
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() => history.push(`/${network.name}/address/${address}`)}
                                        >
                                            {address}
                                        </span>
                                    </text>
                                </div>
                            )}
                            <div className='page-title-border' />
                        </div>
                    </Row>
                    <Boxes items={statisticsData} />
                    <div
                        className='box table-section'
                        style={{ padding: transactionsData?.totalItems == 0 ? '0 24px' : '24px 24px 0' }}
                    >
                        {loading ? (
                            <div
                                className='d-flex justify-content-center align-items-center'
                                style={{ height: '300px' }}
                            >
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <>
                                {transactionsData?.totalItems == 0 ? (
                                    <TransactionsTable data={transactionsData?.items} addressDisabled={address} />
                                ) : (
                                    <>
                                        <TableHeader
                                            title={title}
                                            subtitle={subtitle}
                                            page={page}
                                            limit={limit}
                                            totalPages={totalPages}
                                        />
                                        <TransactionsTable data={transactionsData?.items} addressDisabled={address} />
                                        <TableFooter
                                            limit={limit}
                                            setLimit={setLimit}
                                            page={page}
                                            setPage={setPage}
                                            totalPages={totalPages}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </Col>
            </Container>
        </div>
    )
}

module.exports = Transactions
